import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useIntl } from "react-intl-compat";

import {
  UpdateSpacePayload,
  useUpdateSpaceMutation,
} from "../../../../components/src/services/spaces.service";
import type { Space } from "../../../../types/spaces.types";

import {
  buildSpaceRulesFormSchema,
  SpaceRulesFormValues,
} from "./SpaceRulesForm/SpaceRulesForm.schema";

/**
 * Builds the form for the space-rules page (house rules, minimum age) and
 * wires up its save action. Only the fields the host actually touched are
 * sent
 */
const useSpaceRulesForm = (space?: Space) => {
  const intl = useIntl();
  const [updateSpace] = useUpdateSpaceMutation();
  const [errorMsg, setErrorMsg] = React.useState<string>();

  const form = useForm<SpaceRulesFormValues>({
    resolver: zodResolver(buildSpaceRulesFormSchema({ intl })),
    values: space
      ? {
          rules: space.rules ?? "",
          minAge: String(space.min_age ?? 0),
        }
      : undefined,
  });

  const onSave = async (): Promise<boolean> => {
    setErrorMsg(undefined);
    let success = false;

    await form.handleSubmit(async (values) => {
      if (!space) {
        return;
      }

      const payload: UpdateSpacePayload = {
        rules: values.rules,
        min_age: Number(values.minAge),
      };

      try {
        await updateSpace({ spaceId: space._id, payload }).unwrap();
        success = true;
      } catch {
        setErrorMsg(
          intl.formatMessage({
            id: "hosting.space_rules.submit_error",
            defaultMessage: "Something went wrong. Please try again.",
          }),
        );
      }
    })();

    return success;
  };

  return { form, onSave, errorMsg };
};

export { useSpaceRulesForm };
